import React, { Suspense, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { CommentWithReplyField, FloatingContainer } from '@contently/toolkit';
import { useResource, useFetcher, useInvalidator } from 'rest-hooks';
import AnnotationResource from '../../resources/AnnotationResource';
import useOutsideClick from '../../hooks/useOutsideClick';
import CommentResource from '../../resources/CommentResource';
import useInvalidateCacheOnUnmount from '../../hooks/useInvalidateCacheOnUnmount';
import useUserContext from '../../hooks/useUserContext';
import useStoryContext from '../../hooks/useStoryContext';
import authorOwnerCheck from '../../utils/authorOwnerCheck';
import ReplyChainContainer from './ReplyChainContainer';
import LoadingSpinner from '../../../utils/shared/LoadingSpinner';
import { getReplyCommentParams, getAnnotationParams } from './utils';
import {
  updateResolvedAnnotation,
  updateAnnotationListCacheBust,
  updateCommentListCacheBust
} from '../../store/textEditorReducer';
import useAvatarBadge from '../../hooks/useAvatarBadge';

export default function TextEditorAnnotationComment({
  activeAnnotation,
  allContentIds,
  yPos,
  hide,
  storyId
}) {
  const { comment } = useResource(AnnotationResource.detailShape(), {
    markId: activeAnnotation
  });
  const { mentionableUsersBasicInfo } = useStoryContext();
  const { permissions } = useUserContext();
  const createComment = useFetcher(CommentResource.createShape());
  useInvalidateCacheOnUnmount(AnnotationResource.detailShape(), {
    markId: activeAnnotation
  });
  const editComment = useFetcher(CommentResource.updateShape());
  const dispatch = useDispatch();

  const invalidateAnnotationList = useInvalidator(AnnotationResource.listShape());
  const activeAnnotationRef = useRef(null);
  useOutsideClick(activeAnnotationRef, hide);
  const [commentBody, setCommentBody] = useState(comment.body);

  const [avatarTooltip, avatarBadge, avatarBadgeColor] = useAvatarBadge(
    comment.authorable.fullName,
    comment.authorIsEmployee,
    comment.authorIsManagingEditor
  );

  if (!comment.id) return null;

  const handleEdit = data => {
    setCommentBody(data.edit);
    editComment({ id: comment.id }, { body: data.edit });
    invalidateAnnotationList(getAnnotationParams(allContentIds));
    dispatch(updateAnnotationListCacheBust(true));
  };

  const handleReply = data => {
    createComment({}, getReplyCommentParams(data, storyId, comment.id), [
      [
        CommentResource.listShape(),
        { parent_id: comment.id },
        (newCommentId, commentIds) => {
          return [...(commentIds || []), newCommentId];
        }
      ]
    ]);
    dispatch(updateCommentListCacheBust(comment.id));
  };

  const handleCancel = e => {
    e.preventDefault();
    hide();
  };

  const handleResolve = id => {
    dispatch(updateResolvedAnnotation(activeAnnotation));
    editComment({ id }, { resolved: 1 });
    invalidateAnnotationList(getAnnotationParams(allContentIds));
    dispatch(updateAnnotationListCacheBust(true));
  };

  return (
    <div ref={activeAnnotationRef}>
      <FloatingContainer right={-420} top={yPos} resolved={!!comment.resolved}>
        <CommentWithReplyField
          id={comment.id}
          body={commentBody}
          avatar={comment.authorable.avatar}
          authorIsOwner={authorOwnerCheck(comment.authorable.id, permissions.id)}
          fullName={comment.authorable.fullName}
          formattedCreatedAt={comment.formattedCreatedAt}
          authorIsEmployee={comment.authorIsEmployee}
          authorIsManagingEditor={comment.authorIsManagingEditor}
          onResolve={() => handleResolve(comment.id)}
          resolved={comment.resolved}
          onEditSubmit={e => handleEdit(e)}
          onReplySubmit={e => handleReply(e)}
          onReplyCancel={e => handleCancel(e)}
          borderSides={false}
          showReplyForm={!comment.resolved}
          users={mentionableUsersBasicInfo}
          avatarTooltip={avatarTooltip}
          authorBadge={avatarBadge}
          authorBadgeColor={avatarBadgeColor}
          borderLeft
        >
          <Suspense fallback={<LoadingSpinner />}>
            <ReplyChainContainer commentId={comment.id} showAllComments />
          </Suspense>
        </CommentWithReplyField>
      </FloatingContainer>
    </div>
  );
}

TextEditorAnnotationComment.defaultProps = {
  hide: null
};

TextEditorAnnotationComment.propTypes = {
  activeAnnotation: PropTypes.string.isRequired,
  allContentIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  yPos: PropTypes.number.isRequired,
  hide: PropTypes.func,
  storyId: PropTypes.number.isRequired
};



// WEBPACK FOOTER //
// ./app/javascript/client/components/textEditor/TextEditorAnnotationComment.jsx